import { Component, Editor } from "grapesjs";

declare global {
  interface Window {
    KeenSlider?: any;
  }
}

const KEEN_BLOCK_ID = "keen-slider";
const EXTERNAL_RESOURCES_ID = "external-resources";
const KEEN_CSS_URL =
  "https://cdn.jsdelivr.net/npm/keen-slider@6.8.6/keen-slider.min.css";
const KEEN_JS_URL =
  "https://cdn.jsdelivr.net/npm/keen-slider@6.8.6/keen-slider.min.js";

type ResourceRecord = {
  id: string;
  value: Array<{ url: string }>;
};

const SLIDE_THEME_COUNT = 6;

const getSlideThemeClass = (index: number) =>
  `number-slide${((index - 1) % SLIDE_THEME_COUNT) + 1}`;

const createSlide = (index: number) => ({
  type: "box",
  attributes: {
    "data-keen-slider-slide": "true",
  },
  classes: ["keen-slider__slide", "keen-slider-slide"],
  components: [
    {
      tagName: "div",
      classes: [
        "keen-slider-slide__content",
        "number-slide",
        getSlideThemeClass(index),
      ],
      components: [
        {
          tagName: "h3",
          components: `Slide ${index}`,
        },
        {
          tagName: "p",
          components: "Add your carousel content here.",
        },
      ],
    },
  ],
});

const getResourceValue = (record: any): Array<{ url: string }> => {
  if (!record) {
    return [];
  }

  if (typeof record.get === "function") {
    return (record.get("value") as Array<{ url: string }>) || [];
  }

  return (record.values?.()[2] as Array<{ url: string }>) || [];
};

const ensureResourceRecord = async (
  editor: Editor,
  recordId: string,
  url: string
) => {
  let dataSource = await editor.DataSources.get(EXTERNAL_RESOURCES_ID);

  if (!dataSource) {
    await editor.DataSources.add({
      id: EXTERNAL_RESOURCES_ID,
      records: [
        {
          id: recordId,
          value: [{ url }],
        },
      ],
    });
    return;
  }

  const existingRecord = dataSource.getRecord(recordId);
  const currentValue = getResourceValue(existingRecord);

  if (currentValue.some((item) => item.url === url)) {
    return;
  }

  const nextRecord: ResourceRecord = {
    id: recordId,
    value: [...currentValue, { url }],
  };

  if (existingRecord) {
    await dataSource.removeRecord(recordId);
  }

  dataSource = await editor.DataSources.get(EXTERNAL_RESOURCES_ID);

  if (dataSource) {
    await dataSource.addRecord(nextRecord);
  }
};

const injectCanvasResource = (
  editor: Editor,
  type: "css" | "javascript",
  url: string
) => {
  const doc = editor.Canvas.getDocument();

  if (!doc?.head) {
    return;
  }

  if (type === "css") {
    const existingLink = doc.head.querySelector(`link[href="${url}"]`);
    if (!existingLink) {
      const linkEl = doc.createElement("link");
      linkEl.rel = "stylesheet";
      linkEl.href = url;
      doc.head.appendChild(linkEl);
    }
    return;
  }

  const existingScript = doc.head.querySelector(`script[src="${url}"]`);
  if (!existingScript) {
    const scriptEl = doc.createElement("script");
    scriptEl.src = url;
    doc.head.appendChild(scriptEl);
  }
};

const ensureKeenAssets = async (editor: Editor) => {
  await ensureResourceRecord(editor, "css", KEEN_CSS_URL);
  await ensureResourceRecord(editor, "javascript", KEEN_JS_URL);
  injectCanvasResource(editor, "css", KEEN_CSS_URL);
  injectCanvasResource(editor, "javascript", KEEN_JS_URL);
};

const findComponentByAttribute = (
  component: Component,
  attributeName: string,
  attributeValue: string
) =>
  component
    .components()
    .find(
      (child: Component) =>
        child.getAttributes()[attributeName] === attributeValue
    ) as Component | undefined;

const findComponentsByAttribute = (
  component: Component,
  attributeName: string,
  attributeValue: string
) =>
  component
    .components()
    .filter(
      (child: Component) =>
        child.getAttributes()[attributeName] === attributeValue
    );

const findClosestComponentByAttribute = (
  component: Component | null | undefined,
  attributeName: string,
  attributeValue: string
) => {
  let current = component;

  while (current) {
    if (current.getAttributes()[attributeName] === attributeValue) {
      return current;
    }

    current = current.parent();
  }

  return undefined;
};

export default (editor: Editor) => {
  editor.CssComposer.addRules(`
    .keen-slider-component {
      position: relative;
    }

    .keen-slider-component .keen-slider {
      min-height: 300px;
      max-height: 100vh;
      overflow: hidden;
    }

    .keen-slider-component .keen-slider__slide {
      min-height: 300px;
      display: flex;
    }

    .keen-slider-component .keen-slider-slide__content {
      display: flex;
      width: 100%;
      min-height: 300px;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 1rem;
      padding: 2rem;
      color: #ffffff;
      text-align: center;
    }

    .keen-slider-component .keen-slider-slide__content h3 {
      margin: 0;
      font-size: clamp(2.5rem, 6vw, 4rem);
      line-height: 1;
      font-weight: 600;
    }

    .keen-slider-component .keen-slider-slide__content p {
      margin: 0;
      font-size: 1rem;
      max-width: 28rem;
    }

    .keen-slider-component .number-slide1 {
      background: linear-gradient(
        128deg,
        rgba(64, 175, 255, 1) 0%,
        rgba(63, 97, 255, 1) 100%
      );
    }

    .keen-slider-component .number-slide2 {
      background: linear-gradient(
        128deg,
        rgba(255, 154, 63, 1) 0%,
        rgba(255, 75, 64, 1) 100%
      );
    }

    .keen-slider-component .number-slide3 {
      background: linear-gradient(
        128deg,
        rgba(189, 255, 83, 1) 0%,
        rgba(43, 250, 82, 1) 100%
      );
    }

    .keen-slider-component .number-slide4 {
      background: linear-gradient(
        128deg,
        rgba(64, 255, 242, 1) 0%,
        rgba(63, 188, 255, 1) 100%
      );
    }

    .keen-slider-component .number-slide5 {
      background: linear-gradient(
        128deg,
        rgba(255, 64, 156, 1) 0%,
        rgba(255, 63, 63, 1) 100%
      );
    }

    .keen-slider-component .number-slide6 {
      background: linear-gradient(
        128deg,
        rgba(64, 76, 255, 1) 0%,
        rgba(174, 63, 255, 1) 100%
      );
    }

    .keen-slider-component .keen-slider-controls {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
      padding: 10px 0 0;
      flex-wrap: wrap;
    }

    .keen-slider-component .keen-slider-button {
      font: inherit;
    }

    .keen-slider-component .keen-slider-button:not(.keen-slider-arrow) {
      border: 1px solid #d1d5db;
      border-radius: 999px;
      background: #ffffff;
      color: #111827;
      cursor: pointer;
      padding: 0.5rem 0.875rem;
    }

    .keen-slider-component .keen-slider-button:focus-visible,
    .keen-slider-component .keen-slider-dot:focus-visible {
      outline: 3px solid #0d6efd;
      outline-offset: 2px;
    }

    .keen-slider-component .keen-slider-dots {
      display: flex;
      padding: 10px 0;
      justify-content: center;
    }

    .keen-slider-component .keen-slider-dot {
      border: none;
      width: 10px;
      height: 10px;
      background: #c5c5c5;
      border-radius: 50%;
      margin: 0 5px;
      padding: 5px;
      cursor: pointer;
    }

    .keen-slider-component .keen-slider-dot.dot--active,
    .keen-slider-component .keen-slider-dot[aria-current="true"] {
      background: #000000;
    }

    .keen-slider-component .keen-slider-arrow {
      width: 30px;
      height: 30px;
      position: absolute;
      top: 135px;
      transform: translateY(-50%);
      background-color: transparent;
      background-position: center;
      background-repeat: no-repeat;
      background-size: 30px 30px;
      border: none;
      cursor: pointer;
      padding: 0;
      z-index: 2;
      font-size: 0;
    }

    .keen-slider-component .arrow--left {
      left: 5px;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='white' d='M16.67 0l2.83 2.829-9.339 9.175 9.339 9.167-2.83 2.829-12.17-11.996z'/%3E%3C/svg%3E");
    }

    .keen-slider-component .arrow--right {
      right: 5px;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='white' d='M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z'/%3E%3C/svg%3E");
    }

    .keen-slider-component .keen-slider-arrow.arrow--disabled {
      cursor: default;
      opacity: 0.7;
    }

    .keen-slider-component .arrow--left.arrow--disabled {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='grey' viewBox='0 0 24 24'%3E%3Cpath d='M16.67 0l2.83 2.829-9.339 9.175 9.339 9.167-2.83 2.829-12.17-11.996z'/%3E%3C/svg%3E");
    }

    .keen-slider-component .arrow--right.arrow--disabled {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='grey' viewBox='0 0 24 24'%3E%3Cpath d='M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z'/%3E%3C/svg%3E");
    }

    .keen-slider-component .keen-slider-status {
      font-size: 0.875rem;
      color: #495057;
    }

    .keen-slider-component .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }
  `);

  editor.BlockManager.add(KEEN_BLOCK_ID, {
    label: "Carousel",
    category: "Basic",
    media: `<i class="fa-sharp fa-light fa-sliders fs-2"></i>`,
    content: {
      type: KEEN_BLOCK_ID,
    },
  });

  editor.on("component:add", (component) => {
    if (component.get("type") === KEEN_BLOCK_ID) {
      void ensureKeenAssets(editor);
    }
  });

  const queueSliderRefresh = (component: Component | null | undefined) => {
    const rootComponent = findClosestComponentByAttribute(
      component,
      "data-keen-slider-root",
      "true"
    );

    const rootEl = rootComponent?.getEl() as HTMLElement | null;
    const frameWindow = editor.Canvas.getWindow();

    if (!rootEl || !frameWindow) {
      return;
    }

    frameWindow.requestAnimationFrame(() => {
      frameWindow.requestAnimationFrame(() => {
        rootEl.dispatchEvent(new Event("keen-slider:refresh"));
      });
    });
  };

  editor.on("component:selected", (component) => {
    queueSliderRefresh(component);
  });

  editor.on("component:update", (component) => {
    queueSliderRefresh(component);
  });

  editor.DomComponents.addType(KEEN_BLOCK_ID, {
    model: {
      defaults: {
        tagName: "section",
        attributes: {
          "data-keen-slider-root": "true",
          role: "region",
          "aria-roledescription": "carousel",
          "aria-label": "Featured carousel",
          tabindex: "0",
        },
        classes: ["keen-slider-component"],
        droppable: false,
        slideCount: 3,
        slidesPerView: 1,
        spacing: 16,
        borderRadius: false,
        loop: false,
        autoplay: false,
        autoplayDelay: 5000,
        ariaLabel: "Featured carousel",
        traits: [
          {
            type: "number",
            name: "slideCount",
            label: "Slides",
            default: 3,
            min: 1,
            changeProp: true,
          },
          {
            type: "number",
            name: "slidesPerView",
            label: "Visible Slides",
            default: 1,
            min: 1,
            changeProp: true,
          },
          {
            type: "number",
            name: "spacing",
            label: "Spacing",
            default: 16,
            min: 0,
            changeProp: true,
          },
          {
            type: "checkbox",
            name: "borderRadius",
            label: "Border Radius",
            default: false,
            changeProp: true,
          },
          {
            type: "checkbox",
            name: "loop",
            label: "Loop",
            default: false,
            changeProp: true,
          },
          {
            type: "checkbox",
            name: "autoplay",
            label: "Autoplay",
            default: false,
            changeProp: true,
          },
          {
            type: "number",
            name: "autoplayDelay",
            label: "Autoplay Delay",
            default: 5000,
            min: 1000,
            changeProp: true,
          },
          {
            type: "text",
            name: "ariaLabel",
            label: "Accessible Label",
            default: "Featured carousel",
            changeProp: true,
          },
        ],
        components: [
          {
            type: "box",
            attributes: {
              "data-keen-slider-viewport": "true",
            },
            classes: ["keen-slider"],
            components: [createSlide(1), createSlide(2), createSlide(3)],
          },
          {
            type: "box",
            attributes: {
              "data-keen-slider-controls": "true",
            },
            classes: ["keen-slider-controls"],
            components: [
              {
                tagName: "button",
                attributes: {
                  type: "button",
                  "data-keen-slider-prev": "true",
                  "aria-label": "Previous slide",
                },
                classes: [
                  "keen-slider-button",
                  "keen-slider-arrow",
                  "arrow",
                  "arrow--left",
                ],
              },
              {
                tagName: "div",
                attributes: {
                  "data-keen-slider-dots": "true",
                  "aria-label": "Carousel pagination",
                },
                classes: ["keen-slider-dots"],
              },
              {
                tagName: "button",
                attributes: {
                  type: "button",
                  "data-keen-slider-next": "true",
                  "aria-label": "Next slide",
                },
                classes: [
                  "keen-slider-button",
                  "keen-slider-arrow",
                  "arrow",
                  "arrow--right",
                ],
              },
              {
                tagName: "button",
                attributes: {
                  type: "button",
                  "data-keen-slider-pause": "true",
                  "aria-label": "Pause slide rotation",
                  "aria-pressed": "false",
                  hidden: "hidden",
                },
                classes: ["keen-slider-button"],
                components: "Pause",
              },
              {
                tagName: "div",
                attributes: {
                  "data-keen-slider-status": "true",
                  "aria-live": "polite",
                  "aria-atomic": "true",
                },
                classes: ["keen-slider-status", "sr-only"],
                components: "Slide 1 of 3",
              },
              {
                tagName: "div",
                attributes: {
                  "data-keen-slider-live": "true",
                  "aria-live": "polite",
                  "aria-atomic": "true",
                },
                classes: ["sr-only"],
              },
            ],
          },
        ],
        script: function (opts: Record<string, string>) {
          const root = this as unknown as HTMLElement & {
            __keenSliderState?: {
              cleanup: () => void;
              slider?: any;
            };
          };
          const frameWindow = root.ownerDocument.defaultView || window;
          const viewport = root.querySelector(
            '[data-keen-slider-viewport="true"]'
          ) as HTMLElement | null;
          const prevButton = root.querySelector(
            '[data-keen-slider-prev="true"]'
          ) as HTMLButtonElement | null;
          const nextButton = root.querySelector(
            '[data-keen-slider-next="true"]'
          ) as HTMLButtonElement | null;
          const dotsContainer = root.querySelector(
            '[data-keen-slider-dots="true"]'
          ) as HTMLElement | null;
          const pauseButton = root.querySelector(
            '[data-keen-slider-pause="true"]'
          ) as HTMLButtonElement | null;
          const status = root.querySelector(
            '[data-keen-slider-status="true"]'
          ) as HTMLElement | null;
          const liveRegion = root.querySelector(
            '[data-keen-slider-live="true"]'
          ) as HTMLElement | null;

          const readBoolean = (value: string | boolean | undefined) =>
            value === true ||
            value === "true" ||
            value === "1" ||
            value === "on";

          const cleanupState = () => {
            if (root.__keenSliderState) {
              root.__keenSliderState.cleanup();
              delete root.__keenSliderState;
            }
          };
          const initializeSlider = () => {
            if (!viewport || typeof frameWindow.KeenSlider === "undefined") {
              return false;
            }

            if (!viewport.id) {
              viewport.id =
                root.id || `keen-slider-${Math.random().toString(36).slice(2, 10)}`;
            }

            const slideElements = Array.from(
              viewport.querySelectorAll('[data-keen-slider-slide="true"]')
            ) as HTMLElement[];
            const prefersReducedMotion = frameWindow.matchMedia(
              "(prefers-reduced-motion: reduce)"
            ).matches;
            const autoplayEnabled =
              readBoolean(opts.autoplay) && !prefersReducedMotion;
            const autoplayDelay = Math.max(
              parseInt(opts.autoplayDelay || "5000"),
              1000
            );
            const perView = Math.max(parseInt(opts.slidesPerView || "1"), 1);
            const spacing = Math.max(parseInt(opts.spacing || "16"), 0);
            const isLoopEnabled = readBoolean(opts.loop);
            let autoplayTimer: number | undefined;
            let autoplayPaused = false;

            const updateAnnouncement = (index: number) => {
              const message = `Slide ${index + 1} of ${slideElements.length}`;
              if (status) {
                status.textContent = message;
              }
              if (liveRegion) {
                liveRegion.textContent = message;
              }
            };

            let slider: any;
            const dotButtons: Array<{
              button: HTMLButtonElement;
              onClick: () => void;
            }> = [];

            const clearDots = () => {
              dotButtons.forEach(({ button, onClick }) => {
                button.removeEventListener("click", onClick);
              });
              dotButtons.length = 0;

              if (dotsContainer) {
                dotsContainer.innerHTML = "";
              }
            };

            const buildDots = (count: number) => {
              if (!dotsContainer) {
                return;
              }

              clearDots();

              for (let index = 0; index < count; index += 1) {
                const dotButton = root.ownerDocument.createElement("button");
                const onClick = () => {
                  const maxIndex = slider?.track?.details?.maxIdx || 0;
                  slider?.moveToIdx(Math.min(index, maxIndex));
                };

                dotButton.type = "button";
                dotButton.className = "keen-slider-dot dot";
                dotButton.setAttribute("aria-label", `Go to slide ${index + 1}`);
                dotButton.addEventListener("click", onClick);
                dotsContainer.appendChild(dotButton);
                dotButtons.push({ button: dotButton, onClick });
              }
            };

            const updateDots = (activeIndex: number) => {
              dotButtons.forEach(({ button }, index) => {
                const isActive = index === activeIndex;
                button.classList.toggle("dot--active", isActive);

                if (isActive) {
                  button.setAttribute("aria-current", "true");
                } else {
                  button.removeAttribute("aria-current");
                }
              });
            };

            const updateArrowState = (instance: any) => {
              const currentIndex = instance.track.details.rel || 0;
              const maxIndex = instance.track.details.maxIdx || 0;
              const hasMultiplePages = maxIndex > 0;
              const prevDisabled =
                !hasMultiplePages || (!isLoopEnabled && currentIndex <= 0);
              const nextDisabled =
                !hasMultiplePages ||
                (!isLoopEnabled && currentIndex >= maxIndex);

              if (prevButton) {
                prevButton.disabled = prevDisabled;
                prevButton.classList.toggle("arrow--disabled", prevDisabled);
              }

              if (nextButton) {
                nextButton.disabled = nextDisabled;
                nextButton.classList.toggle("arrow--disabled", nextDisabled);
              }

              if (dotsContainer) {
                if (hasMultiplePages) {
                  dotsContainer.removeAttribute("hidden");
                } else {
                  dotsContainer.setAttribute("hidden", "hidden");
                }
              }
            };

            const syncUi = (instance: any) => {
              const currentIndex = instance.track.details.rel || 0;
              const pageCount = Math.max(
                (instance.track.details.maxIdx || 0) + 1,
                1
              );

              if (pageCount !== dotButtons.length) {
                buildDots(pageCount);
              }

              updateAnnouncement(currentIndex);
              updateDots(currentIndex);
              updateArrowState(instance);
            };

            try {
              slider = new frameWindow.KeenSlider(`#${viewport.id}`, {
                loop: isLoopEnabled,
                slides: {
                  perView,
                  spacing,
                },
                created(instance: any) {
                  syncUi(instance);
                },
                slideChanged(instance: any) {
                  syncUi(instance);
                },
              });
            } catch (error) {
              console.error("Failed to initialize Keen Slider", error);
              return false;
            }

            const refreshSlider = () => {
              frameWindow.requestAnimationFrame(() => {
                frameWindow.requestAnimationFrame(() => {
                  slider?.update();
                  if (slider) {
                    syncUi(slider);
                  }
                });
              });
            };

            const stopAutoplay = () => {
              if (autoplayTimer) {
                frameWindow.clearInterval(autoplayTimer);
                autoplayTimer = undefined;
              }
            };

            const startAutoplay = () => {
              stopAutoplay();
              if (!autoplayEnabled || autoplayPaused) {
                return;
              }

              autoplayTimer = frameWindow.setInterval(() => {
                slider.next();
              }, autoplayDelay);
            };

            const onKeyDown = (event: KeyboardEvent) => {
              if (event.key === "ArrowLeft") {
                event.preventDefault();
                slider.prev();
              }

              if (event.key === "ArrowRight") {
                event.preventDefault();
                slider.next();
              }
            };

            const onPrevClick = () => slider.prev();
            const onNextClick = () => slider.next();
            const onPauseClick = () => {
              autoplayPaused = !autoplayPaused;
              pauseButton?.setAttribute("aria-pressed", String(autoplayPaused));
              if (pauseButton) {
                pauseButton.textContent = autoplayPaused ? "Resume" : "Pause";
                pauseButton.setAttribute(
                  "aria-label",
                  autoplayPaused
                    ? "Resume slide rotation"
                    : "Pause slide rotation"
                );
              }

              if (autoplayPaused) {
                stopAutoplay();
              } else {
                startAutoplay();
              }
            };

            const pauseAutoplay = () => {
              if (!autoplayEnabled) {
                return;
              }

              stopAutoplay();
            };

            const resumeAutoplay = () => {
              if (!autoplayEnabled || autoplayPaused) {
                return;
              }

              startAutoplay();
            };

            root.addEventListener("keydown", onKeyDown);
            root.addEventListener("mouseenter", pauseAutoplay);
            root.addEventListener("mouseleave", resumeAutoplay);
            root.addEventListener("focusin", pauseAutoplay);
            root.addEventListener("focusout", resumeAutoplay);
            root.addEventListener("keen-slider:refresh", refreshSlider);
            prevButton?.addEventListener("click", onPrevClick);
            nextButton?.addEventListener("click", onNextClick);
            pauseButton?.addEventListener("click", onPauseClick);

            if (pauseButton) {
              if (autoplayEnabled) {
                pauseButton.removeAttribute("hidden");
              } else {
                pauseButton.setAttribute("hidden", "hidden");
              }
            }

            startAutoplay();

            root.__keenSliderState = {
              slider,
              cleanup: () => {
                stopAutoplay();
                root.removeEventListener("keydown", onKeyDown);
                root.removeEventListener("mouseenter", pauseAutoplay);
                root.removeEventListener("mouseleave", resumeAutoplay);
                root.removeEventListener("focusin", pauseAutoplay);
                root.removeEventListener("focusout", resumeAutoplay);
                root.removeEventListener("keen-slider:refresh", refreshSlider);
                prevButton?.removeEventListener("click", onPrevClick);
                nextButton?.removeEventListener("click", onNextClick);
                clearDots();
                pauseButton?.removeEventListener("click", onPauseClick);
                slider.destroy();
              },
            };

            return true;
          };

          cleanupState();

          if (!viewport) {
            return;
          }

          if (initializeSlider()) {
            return;
          }

          let attempts = 0;
          const waitForKeen = frameWindow.setInterval(() => {
            attempts += 1;

            if (initializeSlider()) {
              frameWindow.clearInterval(waitForKeen);
            }

            if (attempts > 20) {
              frameWindow.clearInterval(waitForKeen);
            }
          }, 250);

          root.__keenSliderState = {
            cleanup: () => {
              frameWindow.clearInterval(waitForKeen);
            },
          };
        },
        "script-props": [
          "slideCount",
          "slidesPerView",
          "spacing",
          "loop",
          "autoplay",
          "autoplayDelay",
          "ariaLabel",
        ],
      },

      init() {
        this.syncRootAttributes();
        this.syncBorderRadius();
        this.syncSlidesCount();
        this.syncPauseButton();
        this.listenTo(this, "change:ariaLabel", this.syncRootAttributes);
        this.listenTo(this, "change:borderRadius", this.syncBorderRadius);
        this.listenTo(this, "change:slideCount", this.syncSlidesCount);
        this.listenTo(this, "change:autoplay", this.syncPauseButton);
      },

      syncRootAttributes() {
        this.addAttributes({
          role: "region",
          "aria-roledescription": "carousel",
          "aria-label": this.get("ariaLabel") || "Featured carousel",
          tabindex: "0",
        });
      },

      syncBorderRadius() {
        const viewport = findComponentByAttribute(
          this,
          "data-keen-slider-viewport",
          "true"
        );

        if (!viewport) {
          return;
        }

        viewport.addAttributes({
          id: `${this.getId()}-viewport`,
        });

        viewport.addStyle({
          "border-radius": this.get("borderRadius") ? "1rem" : "0",
        });
      },

      syncPauseButton() {
        const controls = findComponentByAttribute(
          this,
          "data-keen-slider-controls",
          "true"
        );
        const pauseButton = controls
          ? findComponentByAttribute(controls, "data-keen-slider-pause", "true")
          : undefined;

        if (!pauseButton) {
          return;
        }

        if (this.get("autoplay")) {
          const attributes = { ...pauseButton.getAttributes() };
          delete attributes.hidden;
          pauseButton.setAttributes(attributes);
        } else {
          pauseButton.addAttributes({ hidden: "hidden" });
        }
      },

      syncSlidesCount() {
        const viewport = findComponentByAttribute(
          this,
          "data-keen-slider-viewport",
          "true"
        );

        if (!viewport) {
          return;
        }

        const desiredCount = Math.max(
          parseInt(`${this.get("slideCount")}` || "1"),
          1
        );
        const slides = findComponentsByAttribute(
          viewport,
          "data-keen-slider-slide",
          "true"
        );

        if (slides.length < desiredCount) {
          for (let index = slides.length; index < desiredCount; index += 1) {
            viewport.append(createSlide(index + 1));
          }
        }

        if (slides.length > desiredCount) {
          slides
            .slice(desiredCount)
            .reverse()
            .forEach((slide) => slide.remove());
        }

        const controls = findComponentByAttribute(
          this,
          "data-keen-slider-controls",
          "true"
        );
        const status = controls
          ? findComponentByAttribute(controls, "data-keen-slider-status", "true")
          : undefined;

        if (status) {
          status.components(`Slide 1 of ${desiredCount}`);
        }
      },
    },
  });
};
